import React, { useState, useRef } from 'react';
import { Card, Button, Alert, ProgressBar } from 'react-bootstrap';

const PDFUpload = ({ onUpload }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError('');
      setResult(null);
    } else {
      setError('Please select a valid PDF file.');
      setFile(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('dragover');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile(droppedFile);
      setError('');
      setResult(null);
    } else {
      setError('Please drop a valid PDF file.');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a PDF file to upload.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError('');

    const formData = new FormData();
    formData.append('pdf', file);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch('/api/upload-pdf', {
        method: 'POST',
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.ok) {
        const data = await response.json();
        setResult(data);
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Refresh the forms list if a form was created
        if (data.formCreated) {
          onUpload && onUpload();
        }
      } else {
        const errorData = await response.json();
        setError('Upload failed: ' + errorData.error);
      }
    } catch (err) {
      setError('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 2000);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setResult(null);
    setError('');
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="form-section">
      <h3>
        <i className="fas fa-upload me-2"></i>
        PDF Upload & Processing
      </h3>
      
      <Card className="mb-4">
        <Card.Header>Upload BAS Form PDF</Card.Header>
        <Card.Body>
          <div
            className="upload-area"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ cursor: 'pointer' }}
          >
            <i className="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
            <h5>Drop your PDF here or click to browse</h5>
            <p className="text-muted">
              Supported format: PDF files only
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {file && (
            <Alert variant="info" className="mt-3">
              <i className="fas fa-file-pdf me-2"></i>
              Selected file: <strong>{file.name}</strong> ({(file.size / 1024 / 1024).toFixed(2)} MB)
            </Alert>
          )}

          {uploading && (
            <div className="mt-3">
              <div className="d-flex justify-content-between mb-2">
                <span>Uploading...</span>
                <span>{uploadProgress}%</span>
              </div>
              <ProgressBar now={uploadProgress} animated />
            </div>
          )}

          {error && (
            <Alert variant="danger" className="mt-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          )}

          {result && (
            <div className="mt-3">
              <Alert variant={result.formCreated ? "success" : "warning"}>
                <i className="fas fa-check-circle me-2"></i>
                PDF processed successfully!
                <br />
                <small>
                  Pages: {result.pages} | 
                  Method: {result.processingMethod === 'ocr' ? 'OCR (Image Recognition)' : 'Text Extraction'}
                </small>
                {result.formCreated ? (
                  <>
                    <br />
                    <i className="fas fa-plus-circle me-1"></i>
                    <strong>Form created successfully!</strong> (ID: {result.formId})
                    <br />
                    <small>Form has been added to your forms list.</small>
                  </>
                ) : (
                  <>
                    <br />
                    <i className="fas fa-exclamation-triangle me-1"></i>
                    <strong>Could not extract form data.</strong>
                    <br />
                    <small>Please create the form manually or check the PDF format.</small>
                  </>
                )}
              </Alert>
              
              {/* Show extracted text for debugging */}
              <Card className="mt-3">
                <Card.Header>
                  <h6 className="mb-0">
                    <i className="fas fa-eye me-2"></i>
                    Extracted Text Preview
                  </h6>
                </Card.Header>
                <Card.Body>
                  <div className="mb-3">
                    <strong>Text Length:</strong> {result.extractedData?.text?.length || 0} characters
                    <br />
                    <strong>Processing Method:</strong> {result.processingMethod}
                  </div>
                  
                  {result.extractedData?.text ? (
                    <div>
                      <h6>First 500 characters:</h6>
                      <pre className="bg-light p-3 rounded" style={{ 
                        maxHeight: '200px', 
                        overflow: 'auto', 
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {result.extractedData.text.substring(0, 500)}
                        {result.extractedData.text.length > 500 && '...'}
                      </pre>
                      
                      {result.extractedData.text.length > 500 && (
                        <small className="text-muted">
                          Showing first 500 characters of {result.extractedData.text.length} total
                        </small>
                      )}
                    </div>
                  ) : (
                    <Alert variant="info">
                      <i className="fas fa-info-circle me-2"></i>
                      No text was extracted from the PDF
                    </Alert>
                  )}
                  
                  {result.formData && (
                    <div className="mt-3">
                      <h6>Detected Form Data:</h6>
                      <pre className="bg-light p-3 rounded" style={{ 
                        maxHeight: '150px', 
                        overflow: 'auto', 
                        fontSize: '12px'
                      }}>
                        {JSON.stringify(result.formData, null, 2)}
                      </pre>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-secondary"
              onClick={resetUpload}
              disabled={uploading}
            >
              <i className="fas fa-times me-1"></i>
              Clear
            </Button>
            
            <Button
              variant="primary"
              onClick={handleUpload}
              disabled={!file || uploading}
            >
              {uploading ? (
                <>
                  <i className="fas fa-spinner fa-spin me-1"></i>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-upload me-1"></i>
                  Upload & Process
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Header>PDF Processing Information</Card.Header>
        <Card.Body>
          <h6>What happens when you upload a PDF:</h6>
          <ul className="mb-3">
            <li>PDF is parsed and text content is extracted</li>
            <li>If no text found, OCR (Optical Character Recognition) is used</li>
            <li>Form fields are automatically detected and populated</li>
            <li>Data is validated for ATO compliance</li>
            <li>Form is saved to the database for further processing</li>
          </ul>
          
          <h6>Supported PDF formats:</h6>
          <ul>
            <li>ATO BAS forms (official templates)</li>
            <li>Custom BAS forms with standard field names</li>
            <li>Scanned PDFs (OCR processing automatically applied)</li>
            <li>Image-based PDFs (converted to images then OCR)</li>
          </ul>
        </Card.Body>
      </Card>
    </div>
  );
};

export default PDFUpload;
